import { useLocation } from '@reach/router'
import Fuse from 'fuse.js'
import { graphql, useStaticQuery } from 'gatsby'
import { DateTime } from 'luxon'
import React, { useState, useEffect } from 'react'
import Card from './card'
import Container from './container'
import Grid from './grid'

export default function FilterArticle({ header }) {
  function sortMapByValue([keyA, valueA], [keyB, valueB]) {
    return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
  }

  function reset() {
    setLocation('')
    setQuery('')
    setTag('')
  }

  const { heading, description } = header ?? {}

  const [location, setLocation] = useState(
    new URLSearchParams(useLocation().search).get('location') ??
      (typeof window !== 'undefined'
        ? window?.sessionStorage?.getItem('location')
        : null) ??
      ''
  )
  const [tag, setTag] = useState('')
  const [query, setQuery] = useState('')

  useEffect(() => {
    window?.sessionStorage?.setItem('location', location)
  }, [location])

  const {
    allWpArticle: { nodes }
  } = useStaticQuery(graphql`
    query {
      allWpArticle {
        nodes {
          contentTypeName
          uri
          date
          acf {
            meta {
              card {
                content {
                  description
                  heading
                  subheading
                  link {
                    ...Link
                  }
                }
                media {
                  video
                  image {
                    ...Image
                  }
                }
              }
              article {
                locations {
                  ... on WpLocation {
                    slug
                    title
                    acf {
                      meta {
                        ...MetaLocation
                      }
                    }
                  }
                }
              }
            }
          }
          ...FeaturedImage
          ...Title
          tags {
            nodes {
              slug
              name
            }
          }
        }
      }
    }
  `)

  let articles = [...nodes]
  let locations = new Map()
  let tags = new Map()

  for (const node of nodes) {
    for (const location of node?.acf?.meta?.article?.locations ?? []) {
      locations.set(location.slug, location)
    }

    for (const { name, slug } of node?.tags?.nodes ?? []) {
      tags.set(slug, name)
    }
  }

  locations = new Map(
    [...locations].sort(([keyA, valueA], [keyB, valueB]) => {
      const dateA = valueA?.acf?.meta?.location?.dates?.[0]
      const dateB = valueB?.acf?.meta?.location?.dates?.[0]

      return dateA < dateB ? -1 : dateA > dateB ? 1 : 0
    })
  )
  locations = new Map([...locations].map(([key, value]) => [key, value.title]))
  tags = new Map([...tags].sort(sortMapByValue))

  const $and = []

  const fields = {
    'acf.meta.article.locations.slug': location,
    'tags.nodes.slug': tag
  }

  query &&
    $and.push({
      $or: [{ title: query }, { 'acf.meta.card.content.heading': query }]
    })

  for (const [key, value] of Object.entries(fields)) {
    value && $and.push({ [key]: `=${value}` })
  }

  if ($and.length) {
    articles = new Fuse(articles, {
      keys: ['title', 'acf.meta.card.content.heading', ...Object.keys(fields)],
      threshold: 0.25,
      useExtendedSearch: true
    })
      .search({ $and })
      .map(({ item }) => item)
  }

  const cards = articles
    .sort(({ date: dateA }, { date: dateB }) =>
      dateA > dateB ? -1 : dateA < dateB ? 1 : 0
    )
    .map((article) => {
      const { card } = article.acf.meta

      return {
        post: article,
        content: {
          kicker: DateTime.fromISO(article.date).toLocaleString(
            DateTime.DATE_FULL
          ),
          heading: card?.content?.heading ?? article.title,
          subheading: card?.content?.subheading,
          description: card?.content?.description,
          link: { url: article.uri }
        },
        tags: article?.tags?.nodes?.map(({ name }) => name),
        media: {
          image: card?.media?.image ?? article?.featuredImage?.node,
          video: card?.media?.video
        }
      }
    })

  return (
    <section className="bg-primary">
      <Container className="max-w-screen-xl py-16 md:pb-12 md:pt-16">
        <Grid>
          <div className="col-span-12 lg:col-span-6">
            {heading && (
              <h2 className="heading-3xl text-secondary">{heading}</h2>
            )}
            {description && (
              <p className="body-bs 420:text-secondary exxxotica:text-tertiary mt-4">
                {description}
              </p>
            )}
          </div>
        </Grid>
      </Container>
      <Container className="max-w-screen-xl">
        <form className="text-secondary -mx-8 md:mx-0">
          <div className="md:rounded-xxx md:border-secondary md:flex md:justify-between md:border md:px-1">
            <div className="scrollbar-hidden flex flex-wrap gap-2 overflow-scroll px-8 md:block md:space-x-0 md:px-0">
              <select
                className="xxx-filter"
                value={location}
                onChange={({ target: { value } }) => {
                  setLocation(value)
                }}>
                <option value="">Location</option>
                {[...locations].map(([value, label], index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <select
                className="xxx-filter"
                value={tag}
                onChange={({ target: { value } }) => {
                  setTag(value)
                }}>
                <option value="">Topic</option>
                {[...tags].map(([value, label], index) => (
                  <option key={index} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <input
                value={query}
                type="text"
                placeholder="Search…"
                className="xxx-filter"
                onInput={({ target: { value } }) => {
                  setQuery(value)
                }}
              />
            </div>
            <button
              className="button button-sm button-dark hidden md:my-1 md:block"
              type="button"
              onClick={reset}>
              Reset filters
            </button>
          </div>
        </form>
        {cards.length ? (
          <ul className="xxx-filter-list">
            {cards.map((card, index) => (
              <li className="col-span-12" key={index}>
                <Card type="horizontal" {...card} />
              </li>
            ))}
          </ul>
        ) : (
          <p>No results found.</p>
        )}
      </Container>
    </section>
  )
}
